import React, { useEffect, useState } from 'react';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import ModalContainer from 'static/script/components/modals/modal-container';
import ModalHeader from 'static/script/components/modals/modal-header';
import ModalBody from 'static/script/components/modals/modal-body';
import { connect } from 'react-redux';
import { closeModal } from 'tradera-state/ui/action-creators';
import { axiosWithTokenRefresh } from 'tradera-utils/api';
import './zign-sec-modal.scss';
import { useTranslator } from 'tradera-lang';
import { LocalLoader } from 'tradera-components/loader/loader';
import { ZignSecLogo } from './zign-sec-logo.jsx';

const STATE = {
    START: 'START',
    VERIFYING: 'VERIFYING',
    ERROR_UNAVAILABLE: 'ERROR_UNAVAILABLE',
    CONTACT_CUSTOMER_SUPPORT: 'CONTACT_CUSTOMER_SUPPORT'
};

const addScriptToDom = zignSecWebSDKUrl => {
    const dataAttributeName = 'data-zign-sec-web-sdk-url-script';
    if (document.querySelector(`[${dataAttributeName}]`)) {
        return;
    }
    const js = document.createElement('script');
    js.setAttribute(dataAttributeName, '1');
    js.setAttribute('type', 'text/javascript');
    js.setAttribute('src', zignSecWebSDKUrl);
    document.body.appendChild(js);
};

const PlainZignSecModal = ({
    closeModal,
    onVerifyComplete,
    zignSecWebSDKUrl
}) => {
    const [verifyingState, setVerifyingState] = useState(STATE.START);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslator();

    useEffect(() => {
        addScriptToDom(zignSecWebSDKUrl);
    }, [zignSecWebSDKUrl]);

    useEffect(() => {
        if (verifyingState !== STATE.VERIFYING) {
            return;
        }

        let currentTimeoutId = null;
        const poll = (zs, sessionId) =>
            new Promise(resolve => {
                const call = () => {
                    currentTimeoutId = setTimeout(async () => {
                        try {
                            // Result will appear when verification is completed.
                            const { result } = await zs.getSessionStatus(
                                sessionId
                            );

                            if (result) {
                                return resolve();
                            }
                        } catch (error) {
                            console.error(error);
                        } finally {
                            call();
                        }
                    }, 2000);
                };
                call();
            });
        setIsLoading(true);
        axiosWithTokenRefresh()
            .get('/api/member-identification/queries/ScanningsCreateWebToken', {
                params: {
                    purpose: 'SYI'
                }
            })
            .then(async ({ data }) => {
                setIsLoading(false);
                const zs = new window.ZignSec(
                    data.token,
                    window.ZsProduct.Scanning
                );

                const r = await zs.openInIframe('zs-root', {
                    relay_state: data.relayState,
                    analysis_types: ['document', 'selfie', 'fraud']
                });
                await poll(zs, r.id);

                setIsLoading(true);
                return await axiosWithTokenRefresh().post(
                    '/api/member-identification/commands/ScanningResult',
                    {
                        sessionId: r.id,
                        relayState: data.relayState
                    }
                );
            })
            .then(({ data }) => {
                setIsLoading(false);

                if (data.isSuccessful) {
                    onVerifyComplete();
                } else {
                    setVerifyingState(STATE.CONTACT_CUSTOMER_SUPPORT);
                }
                return;
            })
            .catch(() => {
                setIsLoading(false);
                setVerifyingState(STATE.ERROR_UNAVAILABLE);
            });
        return () => currentTimeoutId && clearTimeout(currentTimeoutId);
    }, [verifyingState, setVerifyingState, setIsLoading, onVerifyComplete]);

    return (
        <DialogModal onDismiss={closeModal} className="test">
            <ModalContainer onClose={closeModal}>
                <ModalHeader
                    className="border-bottom mt-0"
                    onClose={closeModal}>
                    <div>{t('zignSecModal_heading')}</div>
                </ModalHeader>
                <ModalBody className="zign-sec-modal justify-content-center">
                    <div className="w-100 h-100 position-relative d-flex justify-content-center p-5">
                        {verifyingState === STATE.START && (
                            <div className="text-center">
                                <h4 className="my-4">
                                    {t('zignSecModal_introHeading')}
                                </h4>
                                <div className="text-left px-4">
                                    <p className="mb-4">
                                        {t('zignSecModal_introText_a')}
                                    </p>
                                    <p>{t('zignSecModal_introText_b')}</p>
                                </div>
                                <ZignSecLogo className="mt-5 mb-3" />
                                <p className="mb-5">
                                    {t('zignSecModal_introText_c')}
                                </p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() =>
                                        setVerifyingState(STATE.VERIFYING)
                                    }>
                                    {t('zignSecModal_buttonText')}
                                </button>
                            </div>
                        )}
                        {isLoading && <LocalLoader />}
                        {verifyingState === STATE.VERIFYING && !isLoading && (
                            <div id="zs-root" className="w-100 h-100" />
                        )}
                        {verifyingState === STATE.ERROR_UNAVAILABLE && (
                            <span className="text-danger">
                                {t('zignSecModal_errorUnavailable')}
                            </span>
                        )}
                        {verifyingState === STATE.CONTACT_CUSTOMER_SUPPORT && (
                            <div className="text-center">
                                <h4 className="my-4">
                                    {t(
                                        'zignSecModal_contactCustomerSupportHeading'
                                    )}
                                </h4>
                                <p
                                    className="mb-4 text-left px-4" // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'zignSecModal_contactCustomerSupportText'
                                        )
                                    }}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={() =>
                                        setVerifyingState(STATE.VERIFYING)
                                    }>
                                    {t('zignSecModal_buttonTryAgain')}
                                </button>
                            </div>
                        )}
                    </div>
                </ModalBody>
            </ModalContainer>
        </DialogModal>
    );
};

const mapStateToProps = state => ({
    zignSecWebSDKUrl: state.initData.pageSpecificData.zignSecWebSDKUrl
});

const mapDispatchToProps = {
    closeModal
};

export const ZignSecModal = connect(
    mapStateToProps,
    mapDispatchToProps
)(PlainZignSecModal);
